// src/redux/Actions/teacherPlacementActions.js
import api from "lib/api/api";
import {
  TM_PL_RESULTS_FETCH_START,
  TM_PL_RESULTS_FETCH_SUCCESS,
  TM_PL_RESULTS_FETCH_ERROR,
  TM_PL_FINALIZE_START,
  TM_PL_FINALIZE_SUCCESS,
  TM_PL_FINALIZE_ERROR,
  TM_PL_ENABLE_RETAKE_START,
  TM_PL_ENABLE_RETAKE_SUCCESS,
  TM_PL_ENABLE_RETAKE_ERROR,
} from "./types";

const mapApiRow = (r) => {
  const mcq = r.mcqScore ?? 0;
  const lesen = r.lesenScore ?? 0;
  const audio = r.audioScore ?? 0;
  const sp = r.spScore ?? mcq + lesen + audio;
  const normalizedScore = r.normalizedScore ?? 0;

  return {
    id: r.evaluationId,
    evaluationId: r.evaluationId,
    sessionId: r.sessionId,
    studentId: r.studentId,
    studentName: r.studentName,
    email: r.email,
    guest: !!r.guest,
    avatarUrl: r.avatarUrl || "",
    submittedAt: r.submittedAt,
    blacklisted: r.blacklisted,
    levelSuggestion: r.levelSuggestion,
    writing: r.writing,

    status: r.status,
    targetLevel: r.targetLevel || null,
    spLevel: r.spLevel || null,
    schreibenLevel: r.schreibenLevel || null,
    finalLevel: r.finalLevel || null,
    conflict: !!r.conflict,
    schreibenScore: r.schreibenScore ?? null,
    normalizedScore,
    allowBooking: r.allowBooking,
    resultStatus: r.resultStatus || null,
    studentMessage: r.studentMessage || "",
    studentMessageEn: r.studentMessageEn || "",
    studentMessageDe: r.studentMessageDe || "",
    studentMessageAr: r.studentMessageAr || "",

    mcqScore: mcq,
    lesenScore: lesen,
    audioScore: audio,
    spScore: sp,

    scores: {
      mcq,
      lesen,
      audio,
      total: normalizedScore,
      rawTotal: sp,
      totalMax: 100,
    },
  };
};

export const fetchPlacementResults = (page = 0, size = 20) => async (dispatch) => {
  try {
    dispatch({ type: TM_PL_RESULTS_FETCH_START });
    const { data } = await api.get("/evaluation/test/submitted", { params: { page, size } });
    const rows = (data?.content || []).map(mapApiRow);
    dispatch({
      type: TM_PL_RESULTS_FETCH_SUCCESS,
      payload: {
        rows,
        page: data?.number ?? page,
        totalPages: data?.totalPages ?? 0,
        totalElements: data?.totalElements ?? 0,
      },
    });
  } catch (err) {
    dispatch({
      type: TM_PL_RESULTS_FETCH_ERROR,
      payload: err?.response?.data?.message || err.message || "Failed to load placement results",
    });
  }
};

/**
 * Finalize by explicit sessionId + studentId (sent as request params).
 * We key the spinner by sessionId.
 */
export const finalizePlacement = (sessionId, studentId, schreibenScore) => async (dispatch) => {
  try {
    dispatch({ type: TM_PL_FINALIZE_START, payload: { sessionId } });
    const { data } = await api.post(
      "/evaluation/test/finalize",
      null,
      { params: {
          sessionId,
          studentId,
          schreibenScore: Number(schreibenScore)
        }
      }
    );

    const ok = data?.success !== false;
    const message = data?.message || (ok ? "Finalized" : "Finalize failed");
    dispatch({ type: TM_PL_FINALIZE_SUCCESS, payload: { sessionId } });
    return { ok, message };
  } catch (err) {
    const msg = err?.response?.data?.message || err.message || "Finalize failed";
    dispatch({ type: TM_PL_FINALIZE_ERROR, payload: { sessionId, error: msg } });
    return { ok: false, message: msg };
  }
};

/**
 * Enable retake for a student's placement test.
 * Backend expects `sessionId` and `studentId` as request params.
 */
export const enableRetake = (sessionId, studentId) => async (dispatch) => {
  try {
    dispatch({ type: TM_PL_ENABLE_RETAKE_START, payload: { sessionId } });
    const { data } = await api.post(
      "/evaluation/test/enable-retake",
      null,
      { params: { sessionId, studentId } }
    );

    const ok = data?.success !== false;
    const message = data?.message || (ok ? "Retake enabled" : "Enable retake failed");
    dispatch({ type: TM_PL_ENABLE_RETAKE_SUCCESS, payload: { sessionId } });
    return { ok, message };
  } catch (err) {
    const msg = err?.response?.data?.message || err.message || "Enable retake failed";
    dispatch({ type: TM_PL_ENABLE_RETAKE_ERROR, payload: { sessionId, error: msg } });
    return { ok: false, message: msg };
  }
};

export const sendPlacementMessage =
  ({ sessionId, studentId, subject, message }) =>
  async () => {
    try {
      const { data } = await api.post("/evaluation/test/send-message", {
        sessionId,
        studentId,
        subject,
        message,
      });
      const ok = data?.success !== false;
      return { ok, message: data?.message || (ok ? "Message sent" : "Message failed") };
    } catch (err) {
      return {
        ok: false,
        message: err?.response?.data?.message || err.message || "Message failed",
      };
    }
  };
